import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type ScheduleItem, type ScheduleLink, formatDateFull } from '@/lib/scheduleUtils';

interface Props {
  ganttEdit: {
    item: ScheduleItem;
    mode: 'move' | 'resize-left' | 'resize-right';
    newStart: string;
    newEnd: string;
    newDuration: number;
  } | null;
  links: ScheduleLink[];
  items: ScheduleItem[];
  onClose: () => void;
  onSave: (recalcDownstream: boolean) => Promise<void>;
  saving: boolean;
  saveError: string;
}

export default function ScheduleGanttEditModal({
  ganttEdit,
  links,
  items,
  onClose,
  onSave,
  saving,
  saveError,
}: Props) {
  const { t } = useTranslation();
  const [recalcDownstream, setRecalcDownstream] = useState(false);

  if (!ganttEdit) return null;

  const successors = links.filter((l) => l.predecessor_item_id === ganttEdit.item.id);

  // Calculate total downstream affected using BFS
  const affectedCount = (() => {
    if (successors.length === 0) return 0;
    const affected = new Set<string>();
    const queue = successors.map((s) => s.successor_item_id);
    const visited = new Set<string>();
    while (queue.length) {
      const id = queue.shift()!;
      if (visited.has(id)) continue;
      visited.add(id);
      affected.add(id);
      const next = links.filter((l) => l.predecessor_item_id === id).map((l) => l.successor_item_id);
      queue.push(...next);
    }
    return affected.size;
  })();

  const hasCompleteAffected = items.some((i) => {
    const q = successors.map((s) => s.successor_item_id);
    const v = new Set<string>();
    while (q.length) {
      const id = q.shift()!;
      if (v.has(id)) continue;
      v.add(id);
      if (i.id === id && i.is_complete) return true;
      const next = links.filter((l) => l.predecessor_item_id === id).map((l) => l.successor_item_id);
      q.push(...next);
    }
    return false;
  });

  const handleSave = async () => {
    await onSave(recalcDownstream);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white w-full max-w-md rounded-3xl overflow-hidden mx-4">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-[#1A1A1A] font-bold text-base">
            {ganttEdit.mode === 'move' ? t('ganttEdit.moveItem') : t('ganttEdit.resizeItem')}
          </h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-[#374151]"></i>
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-[#6B7280] text-sm leading-relaxed">
            {ganttEdit.mode === 'move' ? t('ganttEdit.moving') : ganttEdit.mode === 'resize-left' ? t('ganttEdit.shiftingStart') : t('ganttEdit.changingDuration')} <strong className="text-[#1A1A1A]">{ganttEdit.item.title}</strong>
          </p>

          <div className="bg-[#F9FAFB] rounded-xl p-3 space-y-1.5 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-[#6B7280] text-xs">{t('ganttEdit.currentStart')}</span>
              <span className="text-[#374151] font-medium text-xs">{formatDateFull(ganttEdit.item.start_date)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[#6B7280] text-xs">{t('ganttEdit.currentEnd')}</span>
              <span className="text-[#374151] font-medium text-xs">{formatDateFull(ganttEdit.item.end_date)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[#6B7280] text-xs">{t('ganttEdit.newStart')}</span>
              <span className="text-[#1A1A1A] font-bold text-xs">{formatDateFull(ganttEdit.newStart)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[#6B7280] text-xs">{t('ganttEdit.newEnd')}</span>
              <span className="text-[#1A1A1A] font-bold text-xs">{formatDateFull(ganttEdit.newEnd)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[#6B7280] text-xs">{t('ganttEdit.duration')}</span>
              <span className="text-[#374151] font-medium text-xs">{ganttEdit.newDuration} {t('ganttEdit.workingDays')}</span>
            </div>
          </div>

          {successors.length > 0 && (
            <div className="bg-[#FEF0E7] rounded-xl p-2.5 border border-[#FDE68A] space-y-1.5">
              <div className="flex items-center gap-2 text-xs text-[#92400E] font-medium">
                <i className="ri-links-line text-[#F59E0B]"></i>
                {t('ganttEdit.hasSuccessors', { n: successors.length, plural: successors.length > 1 ? 's' : '' })}
              </div>
              <p className="text-[#92400E] text-xs">
                {t('ganttEdit.downstreamAffected', { n: affectedCount, plural: affectedCount > 1 ? 's' : '' })}
              </p>
              {hasCompleteAffected && (
                <div className="flex items-start gap-1.5 text-xs text-[#EF4444]">
                  <i className="ri-error-warning-line mt-0.5"></i>
                  {t('ganttEdit.someComplete')}
                </div>
              )}
              <label className="flex items-center gap-2 cursor-pointer pt-1">
                <input
                  type="checkbox"
                  checked={recalcDownstream}
                  onChange={(e) => setRecalcDownstream(e.target.checked)}
                  className="w-4 h-4 accent-[#E8621A]"
                />
                <span className="text-xs text-[#92400E]">{t('ganttEdit.recalcDownstream')}</span>
              </label>
            </div>
          )}

          {(ganttEdit.item.is_complete || ganttEdit.item.progress >= 100) && (
            <div className="bg-[#FEF2F2] rounded-xl p-2.5 border border-[#FECACA] flex items-start gap-2">
              <i className="ri-error-warning-line text-[#EF4444] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-[#EF4444] text-xs">
                {t('ganttEdit.itemComplete')}
              </p>
            </div>
          )}

          {ganttEdit.item.manual_date_lock && (
            <div className="bg-[#F5F3FF] rounded-xl p-2.5 border border-[#DDD6FE] flex items-start gap-2">
              <i className="ri-lock-line text-[#7C3AED] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-[#5B21B6] text-xs">
                {t('ganttEdit.manualLock')}
              </p>
            </div>
          )}

          {saveError && <p className="text-[#EF4444] text-xs bg-[#FEF2F2] rounded-xl px-3 py-2">{saveError}</p>}
        </div>
        <div className="px-5 py-4 border-t border-gray-100 flex flex-col gap-2">
          {successors.length > 0 ? (
            <>
              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full py-3 rounded-xl bg-[#E8621A] text-white text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? <><i className="ri-loader-4-line animate-spin"></i>...</> : (recalcDownstream ? t('ganttEdit.saveAndUpdate') : t('ganttEdit.saveOnly'))}
              </button>
              <button
                onClick={onClose}
                className="w-full py-2 rounded-xl text-[#9CA3AF] text-xs font-bold cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
            </>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="flex-1 py-3 rounded-xl bg-white border border-gray-200 text-[#374151] text-sm font-bold cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-3 rounded-xl bg-[#E8621A] text-white text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? <><i className="ri-loader-4-line animate-spin"></i>...</> : 'Save'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}